/**
 * @fileoverview Composable for chat session management
 * @module composables/useChatSession
 *
 * Manages session lifecycle including start, end, and state tracking.
 *
 * @vue3migration - Phase 3C: Extracted from ChatWindow/Index.vue
 */
import { ref, computed, type ComputedRef } from 'vue'
import * as _ from 'lodash-es'

/**
 * Store interface for session-related operations
 */
export interface SessionStore {
  sessionEnded: boolean
  sessionButtonReady: boolean
  settings: {
    endSessionButton?: { enabled?: boolean }
    showPostChatForm?: { enabled?: boolean }
    liveChatStatus?: boolean
  }
  setEndSession: (val: boolean) => void
  stopLivechat: () => Promise<void>
  logout: () => Promise<void>
  clearCache: () => Promise<void>
  register: () => Promise<void>
  login: () => Promise<void>
  listen: () => Promise<void>
  setGreeted: (val: boolean) => void
  sendStartMessage: () => Promise<void>
  setDisableTextInput: (val: boolean) => Promise<void>
  endSession: () => Promise<void>
  sendPostback: (payload: { text: string; event: string; data: string }) => void
}

/**
 * Options for useChatSession composable
 */
interface UseChatSessionOptions {
  /** Pinia store instance */
  store: SessionStore
  /** Callback when endChat is triggered */
  onEndChat?: () => void
}

/**
 * Composable for chat session management
 *
 * @param options - Dependencies and callbacks
 * @returns Session state and control methods
 *
 * @example
 * ```ts
 * const {
 *   sessionEnded,
 *   endButtonLoading,
 *   endSession,
 *   startSession,
 * } = useChatSession({
 *   store: useMainStore(),
 *   onEndChat: () => { ... },
 * })
 * ```
 */
export function useChatSession(options: UseChatSessionOptions) {
  const { store, onEndChat } = options

  // ====================
  // State
  // ====================

  /** Loading state for session buttons */
  const endButtonLoading = ref(false)

  // ====================
  // Computed
  // ====================

  /**
   * Whether end session button is enabled in settings
   */
  const endSessionButtonEnabled = computed(() =>
    _.get(store.settings, 'endSessionButton.enabled', false)
  )

  /**
   * Whether post-chat form is enabled in settings
   */
  const postChatFormEnabled = computed(() =>
    _.get(store.settings, 'showPostChatForm.enabled', false)
  )

  /**
   * Whether session button should be shown
   */
  const sessionButtonReady = computed(() =>
    endSessionButtonEnabled.value && store.sessionButtonReady
  )

  /**
   * Session ended state (getter/setter)
   * Syncs with store state
   */
  const sessionEnded = computed({
    get: () => store.sessionEnded,
    set: (val: boolean) => store.setEndSession(val),
  })

  // ====================
  // Internal Methods
  // ====================

  /**
   * Stop session - logout and optionally clear cache
   */
  async function _stop({ with_clear_cache = false }: { with_clear_cache?: boolean } = {}) {
    await store.stopLivechat()
    await store.logout()
    if (with_clear_cache) {
      await store.clearCache()
    }
  }

  /**
   * Start session - register, login, and initialize
   */
  async function _start({
    with_register = false,
    with_login = false,
  }: {
    with_register?: boolean
    with_login?: boolean
  } = {}) {
    if (with_register) {
      await store.register()
    }
    if (with_login) {
      await store.login()
    }
    await store.listen()
    store.setGreeted(true)
    await store.sendStartMessage()
    await store.setDisableTextInput(false)
  }

  // ====================
  // Public Methods
  // ====================

  /**
   * End live chat session
   */
  async function endLiveChat() {
    store.sendPostback({
      text: 'End Live Chat',
      event: 'handover_stop',
      data: 'user',
    })
    await store.stopLivechat()
  }

  /**
   * End current session
   * Handles live chat, post-chat form, or regular end session
   */
  async function endSession() {
    endButtonLoading.value = true
    try {
      if (store.settings.liveChatStatus) {
        await endLiveChat()
      } else if (postChatFormEnabled.value) {
        onEndChat?.()
      } else {
        await store.endSession()
        sessionEnded.value = true
      }
    } finally {
      endButtonLoading.value = false
    }
  }

  /**
   * Start a new session after ending previous
   */
  async function startSession() {
    endButtonLoading.value = true
    try {
      await _stop({})
      sessionEnded.value = false
      await _start({ with_register: false, with_login: true })
    } finally {
      endButtonLoading.value = false
    }
  }

  /**
   * Renew session (alias for store.start)
   */
  async function renewSession() {
    // This would call store.start() but keeping it simple for now
    await _start({ with_login: true })
  }

  return {
    // State
    endButtonLoading,

    // Computed
    endSessionButtonEnabled,
    postChatFormEnabled,
    sessionButtonReady,
    sessionEnded,

    // Internal (exposed for component use)
    _start,
    _stop,

    // Methods
    endLiveChat,
    endSession,
    startSession,
    renewSession,
  }
}
